/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import {
    m11ReportContainerFoiChanged, m11ReportContainerResourceChanged,
    m11ResearchStudyResourceChanged, selectM11ReportContainerFoi, selectM11ReportContainerResource,
    selectM11ResearchStudyResource
} from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import jsonEquals from "../../functions/jsonEquals";
import getIdFromReference from "../../functions/getIdFromReference";
import { displayStringFromFHIRIdentifier } from "../../datatypeComponents/IdentifierEntry";
import { displayStringFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import { getCodeableConceptDisplayForObjectOrArray, simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRQuantity } from "../../datatypeComponents/QuantityEntry";
import loadResource from "../m11ReportFunctions/loadResource";
import TextDivEntry from "./TextDivEntry";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";
import StringEntry from "../../datatypeComponents/StringEntry";
import { Button } from "semantic-ui-react";

const saveTitlePageChanges = async (dispatch, apiToken, m11ResearchStudyFoi,
    titlePageState, setAlertModalState, setStartingTitlePageState) => {

    let changesStates = {
        titlePageState: titlePageState
    };
    let response = await saveM11Changes(apiToken, 5, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ResearchStudyResource) {
            dispatch(m11ResearchStudyResourceChanged(response.m11ResearchStudyResource));
        }
        if (response.m11ReportContainerResource) {
            dispatch(m11ReportContainerResourceChanged(response.m11ReportContainerResource));
        }
        setStartingTitlePageState(JSON.parse(JSON.stringify(titlePageState)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step005CreateTitlePage = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ReportContainerFoi = useSelector(selectM11ReportContainerFoi);
    const m11ReportContainerResource = useSelector(selectM11ReportContainerResource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [titlePageState, setTitlePageState] = useState({
        "status": "empty", "titlePageXhtml": ""
    });
    const [startingTitlePageState, setStartingTitlePageState] = useState({
        "status": "empty", "titlePageXhtml": ""
    });
    const [sponsorDataState, setSponsorDataState] = useState({
        "sponsorApprovalDate": "", "sponsorSignatory": ""
    });

    const [initialCalculationNeeded, setInitialCalculationNeeded] = useState(false);
    const [remount, setRemount] = useState(1);

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(titlePageState, startingTitlePageState)),
        async saveAllChanges() {
            await saveTitlePageChanges(dispatch, apiToken, m11ResearchStudyFoi,
                titlePageState, setAlertModalState, setStartingTitlePageState);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource?.id) {
            let m11ReportFoi = "";
            if (m11ResearchStudyResource.relatesTo?.length > 0) {
                for (let relatesTo of m11ResearchStudyResource.relatesTo) {
                    if (relatesTo.type?.coding?.[0]?.code === "supported-with" && relatesTo.type.text === "M11 Report" &&
                        relatesTo.targetReference) {
                        m11ReportFoi = getIdFromReference(relatesTo.targetReference);
                    }
                }
            }
            if (m11ReportFoi && !isNaN(m11ReportFoi) && m11ReportFoi !== m11ReportContainerFoi) {
                dispatch(m11ReportContainerFoiChanged(m11ReportFoi));
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ResearchStudyResource]);

    useEffect(() => {
        if (initialCalculationNeeded === true) {
            setTitlePageState({
                "status": "empty", "titlePageXhtml": "", "loaded": true
            });
            setStartingTitlePageState({
                "status": "empty", "titlePageXhtml": "", "loaded": true
            });
        }
    }, [initialCalculationNeeded]);

    useEffect(() => {
        if ((titlePageState.sponsorApprovalDate &&
            !titlePageState.titlePageXhtml.includes(titlePageState.sponsorApprovalDate)
        ) ||
            (titlePageState.sponsorSignatory &&
                !titlePageState.titlePageXhtml.includes(titlePageState.sponsorSignatory)
            )) {
            let titlePageXhtml = "<div xmlns=\"http://www.w3.org/1999/xhtml\">";
            titlePageXhtml += "<table>";
            let sponsorConfidentialityStatement;
            let studyAmendment;
            if (m11ResearchStudyResource.extension) {
                for (const extension of m11ResearchStudyResource.extension) {
                    if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement" &&
                        extension.valueString) {
                        sponsorConfidentialityStatement = extension.valueString;
                    }
                    if (extension.url === "http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/m11-protocol-amendment" &&
                        extension.extension.length > 0) {
                        studyAmendment = extension;
                    }
                }
            }
            if (sponsorConfidentialityStatement) {
                titlePageXhtml += "<tr><td><strong>Sponsor Confidentiality Statement:</strong></td><td>" + sponsorConfidentialityStatement + "</td></tr>";
            }
            titlePageXhtml += "<tr><td><strong>Full Title:</strong></td><td>" + m11ResearchStudyResource.title + "</td></tr>";
            let acronym;
            let shortTitle;
            if (m11ResearchStudyResource.label?.length > 0) {
                for (const label of m11ResearchStudyResource.label) {
                    if (label.type?.coding?.[0]?.code === "acronym") {
                        acronym = label.value;
                    } else if (label.type?.coding?.[0]?.code === "short-title") {
                        shortTitle = label.value;
                    }
                }
            }
            if (acronym) {
                titlePageXhtml += "<tr><td><strong>Trial Acronym:</strong></td><td>" + acronym + "</td></tr>";
            }
            let sponsorProtocolIdentifier;
            let nctid;
            let euCTnumber;
            let fdaINDnumber;
            let ideNumber;
            let jRCTnumber;
            let nmpaINDnumber;
            let whoUTNnumber;
            let otherIdentifiers = [];
            if (m11ResearchStudyResource.identifier) {
                for (const identifier of m11ResearchStudyResource.identifier) {
                    if (identifier.system === "https://fevir.net/FOI") {
                        //
                    } else if (identifier.system === "https://clinicaltrials.gov/nctid" || identifier.type?.coding?.[0]?.code === "C172240") {
                        nctid = "ClinicalTrials.gov NCTID " + identifier.value;
                    } else if (identifier.system === "https://euclinicaltrials.eu/clinicalTrialId") {
                        euCTnumber = "EU CTIS clinicalTrialId " + identifier.value;
                    } else if (identifier.type?.text === "Sponsor Protocol Identifier" || identifier.type?.coding?.[0]?.code === "C132351") {
                        sponsorProtocolIdentifier = identifier.value;
                    } else if (identifier.type?.text === "FDA IND Number" || identifier.type?.coding?.[0]?.code === "C218685") {
                        fdaINDnumber = "FDA IND Number " + identifier.value;
                    } else if (identifier.type?.text === "IDE Number" || identifier.type?.coding?.[0]?.code === "C218686") {
                        ideNumber = "IDE Number " + identifier.value;
                    } else if (identifier.type?.text === "jRCT Number" || identifier.type?.coding?.[0]?.code === "C218687") {
                        jRCTnumber = "jRCT Number " + identifier.value;
                    } else if (identifier.type?.text === "NMPA IND Number" || identifier.type?.coding?.[0]?.code === "C218688") {
                        nmpaINDnumber = "NMPA IND Number " + identifier.value;
                    } else if (identifier.type?.text === "WHO/UTN Number" || identifier.type?.coding?.[0]?.code === "C218689") {
                        whoUTNnumber = "WHO/UTN Number " + identifier.value;
                    } else {
                        otherIdentifiers.push(displayStringFromFHIRIdentifier(identifier));
                    }
                }
            }
            titlePageXhtml += "<tr><td><strong>Sponsor Protocol Identifier:</strong></td><td>" +
                (sponsorProtocolIdentifier ? sponsorProtocolIdentifier : "[Replace with a unique alphanumeric identifier for the trial, designated by the sponsor.]") +
                "</td></tr>";
            titlePageXhtml += "<tr><td><strong>Original Protocol:</strong></td><td>" +
                (studyAmendment ? "No (C49487)" : "Yes (C49488)") +
                "</td></tr>";
            if (m11ResearchStudyResource.version) {
                titlePageXhtml += "<tr><td><strong>Version Number:</strong></td><td>" +
                    m11ResearchStudyResource.version +
                    "</td></tr>";
            }
            if (m11ResearchStudyResource.date) {
                titlePageXhtml += "<tr><td><strong>Version Date:</strong></td><td>" +
                    m11ResearchStudyResource.date +
                    "</td></tr>";
            }
            let amendmentIdentifier;
            let amendmentPrevious;
            let amendmentScope;
            let amendmentCountry = [];
            let amendmentRegion = [];
            let amendmentSite = [];
            let amendmentApprovalDate;
            let amendmentSignature;
            let amendmentsignatureUrlString;
            let amendmentsignatureUrlUrl;
            let amendmentsignatureMethod;
            let amendmentScopeImpact = [];
            let amendmentPrimaryReason;
            let amendmentSecondaryReason = [];
            let amendmentSummary;
            let substantialImpactSafety;
            let substantialImpactSafetyComment;
            let substantialImpactReliability;
            let substantialImpactReliabilityComment;
            let amendmentDetails = [];
            let amendmentRationale;
            let amendmentDescription;
            if (studyAmendment) {
                for (const ext of studyAmendment.extension) {
                    if (ext.url === "identifier" && ext.valueIdentifier) {
                        amendmentIdentifier = ext.valueIdentifier;
                    } else if (ext.url === "previous" && ext.valueCodeableConcept) {
                        amendmentPrevious = ext.valueCodeableConcept;
                    } else if (ext.url === "scope" && ext.valueCodeableConcept) {
                        amendmentScope = ext.valueCodeableConcept;
                    } else if (ext.url === "country" && ext.valueCodeableConcept) {
                        amendmentCountry.push(ext.valueCodeableConcept);
                    } else if (ext.url === "region" && ext.valueCodeableConcept) {
                        amendmentRegion.push(ext.valueCodeableConcept);
                    } else if (ext.url === "site" && ext.valueIdentifier) {
                        amendmentSite.push(displayStringFromFHIRIdentifier(ext.valueIdentifier));
                    } else if (ext.url === "site" && ext.valueReference) {
                        amendmentSite.push(displayStringFromFHIRReference(ext.valueReference));
                    } else if (ext.url === "approvalDate" && ext.valueDate) {
                        amendmentApprovalDate = ext.valueDate;
                    } else if (ext.url === "signature" && ext.valueSignature) {
                        amendmentSignature = ext.valueSignature;
                    } else if (ext.url === "signatureUrl" && ext.valueString) {
                        amendmentsignatureUrlString = ext.valueString;
                    } else if (ext.url === "signatureUrl" && ext.valueUrl) {
                        amendmentsignatureUrlUrl = ext.valueUrl;
                    } else if (ext.url === "signatureMethod" && ext.valueString) {
                        amendmentsignatureMethod = ext.valueString;
                    } else if (ext.url === "http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/m11-amendment-scope-impact" && ext.extension) {
                        amendmentScopeImpact.push(ext);
                    } else if (ext.url === "primaryReason" && ext.valueCodeableConcept) {
                        amendmentPrimaryReason = ext.valueCodeableConcept;
                    } else if (ext.url === "secondaryReason" && ext.valueCodeableConcept) {
                        amendmentSecondaryReason.push(ext.valueCodeableConcept);
                    } else if (ext.url === "summary" && ext.valueString) {
                        amendmentSummary = ext.valueString;
                    } else if (ext.url === "substantialImpactSafety" && ext.valueCodeableConcept) {
                        substantialImpactSafety = ext.valueCodeableConcept;
                    } else if (ext.url === "substantialImpactSafetyComment" && ext.valueString) {
                        substantialImpactSafetyComment = ext.valueString;
                    } else if (ext.url === "substantialImpactReliability" && ext.valueCodeableConcept) {
                        substantialImpactReliability = ext.valueCodeableConcept;
                    } else if (ext.url === "substantialImpactReliabilityComment" && ext.valueString) {
                        substantialImpactReliabilityComment = ext.valueString;
                    } else if (ext.url === "http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/m11-amendment-detail" && ext.extension) {
                        amendmentDetails.push(ext);
                    } else if (ext.url === "rationale" && ext.valueString) {
                        amendmentRationale = ext.valueString;
                    } else if (ext.url === "description" && ext.valueMarkdown) {
                        amendmentDescription = ext.valueMarkdown;
                    } else if (ext.url === "description" && ext.valueString) {
                        amendmentDescription = ext.valueString;
                    }
                }
            }
            if (amendmentIdentifier?.value) {
                titlePageXhtml += "<tr><td><strong>Amendment Identifier:</strong></td><td>" + amendmentIdentifier.value + "</td></tr>";
            }
            if (amendmentScope) {
                titlePageXhtml += "<tr><td><strong>Amendment Scope:</strong></td><td><p>" +
                    simpleDisplayStringFromFHIRCodeableConcept(amendmentScope) + "</p>" +
                    (amendmentCountry.length > 0 ?
                        "<p>Country specific: " + getCodeableConceptDisplayForObjectOrArray(amendmentCountry) + "</p>"
                        :
                        "") +
                    (amendmentRegion.length > 0 ?
                        "<p>Region Specific: " + getCodeableConceptDisplayForObjectOrArray(amendmentRegion) + "</p>"
                        :
                        "") +
                    (amendmentSite.length > 0 ?
                        "<p>Site specific: " + amendmentSite.join(", ") + "</p>"
                        :
                        "") +
                    "</td></tr>";
            }
            let productCode = [];
            let productNames = [];
            if (m11ResearchStudyResource.focus?.length) {
                for (const focus of m11ResearchStudyResource.focus) {
                    if (focus.concept?.coding?.[0]?.system === "ForUseWithM11ReportAuthoringTool" &&
                        focus.concept.text) {
                        if (focus.concept.coding[0].display === "Sponsor's Product Code") {
                            productCode.push(focus.concept.text);
                        } else if (focus.concept.coding[0].display === "Product Proprietary Name") {
                            productNames.push(focus.concept.text);
                        } else if (focus.concept.coding[0].display === "Product Nonproprietary Name") {
                            productNames.push(focus.concept.text);
                        }
                    }
                }
            }
            if (productCode.length) {
                titlePageXhtml += "<tr><td><strong>Sponsor’s Investigational Product Code(s):</strong></td><td><p>" +
                    productCode.join("</p><p>") +
                    "</p></td></tr>";
            }
            if (productNames.length) {
                titlePageXhtml += "<tr><td><strong>Investigational Product Name(s):</strong></td><td>" +
                    productNames.join("</p><p>") +
                    "</td></tr>";
            }
            let trialPhase = m11ResearchStudyResource.phase?.coding?.[0]?.display ||
                m11ResearchStudyResource.phase?.text;
            titlePageXhtml += "<tr><td><strong>Trial Phase:</strong></td><td>" +
                (trialPhase ? trialPhase : "[Enter the Trial Phase]") +
                "</td></tr>";
            if (shortTitle) {
                titlePageXhtml += "<tr><td><strong>Short Title:</strong></td><td>" + shortTitle + "</td></tr>";
            }
            let sponsorName;
            let sponsorAddress;
            let cosponsors = [];
            let localSponsors = [];
            let manufacturers = [];
            let medicalExpertContact;
            if (m11ResearchStudyResource.associatedParty) {
                for (const associated of m11ResearchStudyResource.associatedParty) {
                    if (associated.role?.coding?.[0]?.code === "lead-sponsor") {
                        sponsorName = associated.name;
                        sponsorAddress = associated.party?.display;
                    } else if (associated.role?.coding?.[0]?.code === "sponsor") {
                        if (associated.role.text?.toLowerCase() === "co-sponsor" ||
                            associated.role.text?.toLowerCase() === "cosponsor") {
                            cosponsors.push({
                                cosponsorName: associated.name,
                                cosponsorAddress: associated.party?.display
                            });
                        } else if (associated.role.text?.toLowerCase() === "local sponsor") {
                            localSponsors.push({
                                localSponsorName: associated.name,
                                localSponsorAddress: associated.party?.display
                            });
                        } else {
                            sponsorName = associated.name;
                            sponsorAddress = associated.party?.display;
                        }
                    } else if (associated.role.text?.toLowerCase() === "device manufacturer") {
                        manufacturers.push({
                            deviceManufacturerName: associated.name,
                            deviceManufacturerAddress: associated.party?.display
                        });
                    } else if (associated.role.text?.toLowerCase() === "medical expert contact") {
                        medicalExpertContact = associated.name;
                    }
                }
            }
            titlePageXhtml += "<tr><td><strong>Sponsor Name and Address:</strong></td><td>";
            titlePageXhtml += "<p>" + (sponsorName ? sponsorName : "[Enter Sponsor Name.]") + "</p>";
            titlePageXhtml += "<p>" + (sponsorAddress ? sponsorAddress : "[Enter Sponsor Legal Address.]") + "</p>";
            if (cosponsors.length > 0) {
                for (const cosponsor of cosponsors) {
                    titlePageXhtml += "<p>Co-Sponsor Name and Address:</p>";
                    titlePageXhtml += "<p>" + cosponsor.cosponsorName + "</p>";
                    titlePageXhtml += "<p>" + cosponsor.cosponsorAddress + "</p>";
                }
            }
            if (localSponsors.length > 0) {
                for (const local of localSponsors) {
                    titlePageXhtml += "<p>Local Sponsor Name and Address:</p>";
                    titlePageXhtml += "<p>" + local.localSponsorName + "</p>";
                    titlePageXhtml += "<p>" + local.localSponsorAddress + "</p>";
                }
            }
            titlePageXhtml += "</td></tr>";
            if (manufacturers.length > 0) {
                for (const manufacturer of manufacturers) {
                    titlePageXhtml += "<tr><td><strong>Device Manufacturer Name and Address:</strong></td><td>" +
                        "<p>" + manufacturer.deviceManufacturerName + "</p>" +
                        "<p>" + manufacturer.deviceManufacturerAddress + "</p>" +
                        "</td></tr>";
                }
            }
            titlePageXhtml += "<tr><td><strong>Regulatory or Clinical Trial Identifier(s):</strong></td><td>";
            if (euCTnumber) {
                titlePageXhtml += "<p>" + euCTnumber + "</p>";
            }
            if (fdaINDnumber) {
                titlePageXhtml += "<p>" + fdaINDnumber + "</p>";
            }
            if (ideNumber) {
                titlePageXhtml += "<p>" + ideNumber + "</p>";
            }
            if (jRCTnumber) {
                titlePageXhtml += "<p>" + jRCTnumber + "</p>";
            }
            if (nctid) {
                titlePageXhtml += "<p>" + nctid + "</p>";
            }
            if (nmpaINDnumber) {
                titlePageXhtml += "<p>" + nmpaINDnumber + "</p>";
            }
            if (whoUTNnumber) {
                titlePageXhtml += "<p>" + whoUTNnumber + "</p>";
            }
            if (otherIdentifiers.length) {
                titlePageXhtml += "<p>" + otherIdentifiers.join("</p><p>") + "</p>";
            }
            titlePageXhtml += "</td></tr>";
            titlePageXhtml += "<tr><td><strong>Sponsor Approval:</strong></td><td>";
            if (titlePageState.sponsorApprovalDate) {
                titlePageXhtml += titlePageState.sponsorApprovalDate;
            } else {
                titlePageXhtml += "[Enter Sponsor Approval Date or State the location where sponsor approval information can be found]";
            }
            titlePageXhtml += "</td></tr>";
            titlePageXhtml += "</table>";
            if (titlePageState.sponsorSignatory) {
                titlePageXhtml += "<p><strong>Sponsor Signatory:</strong> " + titlePageState.sponsorSignatory + "</p>";
            }
            if (medicalExpertContact) {
                titlePageXhtml += "<p><strong>Medical Expert Contact:</strong> " + medicalExpertContact + "</p>";
            }
            titlePageXhtml += "<p><strong>Amendment Details</strong></p>";
            if (!studyAmendment) {
                titlePageXhtml += "<p>This protocol has not been amended.</p>";
            } else {
                if (amendmentPrevious?.coding?.[0]?.code === "C218486" ||
                    amendmentPrevious?.coding?.[0]?.display === "First Protocol Amendment") {
                    titlePageXhtml += "<p>This is the first protocol amendment.</p>";
                } else {
                    titlePageXhtml += "<p>This protocol has been amended previously. Details of prior amendments are presented in Section 12.3 Prior Protocol Amendment(s).</p>";
                }
                titlePageXhtml += "<p>Current Amendment</p>";
                titlePageXhtml += "<table>";
                let numberOrPercent;
                let scope1number;
                let scope1definition;
                let scope2number;
                let scope2definition;
                let scope3number;
                let scope3definition;
                if (amendmentScopeImpact.length > 0) {
                    if (amendmentScopeImpact[0].extension.length) {
                        for (const extension of amendmentScopeImpact[0].extension) {
                            if (extension.url === "number") {
                                if (extension.valuePositiveInt) {
                                    scope1number = extension.valuePositiveInt;
                                } else if (extension.valueQuantity) {
                                    numberOrPercent = "percent";
                                    scope1number = displayStringFromFHIRQuantity(extension.valueQuantity);
                                }
                            }
                            if (extension.url === "scope") {
                                scope1definition = simpleDisplayStringFromFHIRCodeableConcept(extension.valueCodeableConcept);
                            }
                        }
                    }
                }
                if (amendmentScopeImpact.length > 1) {
                    if (amendmentScopeImpact[1].extension.length) {
                        for (const extension of amendmentScopeImpact[1].extension) {
                            if (extension.url === "number") {
                                if (extension.valuePositiveInt) {
                                    scope2number = extension.valuePositiveInt;
                                } else if (extension.valueQuantity) {
                                    scope2number = displayStringFromFHIRQuantity(extension.valueQuantity);
                                }
                            }
                            if (extension.url === "scope") {
                                scope2definition = simpleDisplayStringFromFHIRCodeableConcept(extension.valueCodeableConcept);
                            }
                        }
                    }
                }
                if (amendmentScopeImpact.length > 2) {
                    if (amendmentScopeImpact[2].extension.length) {
                        for (const extension of amendmentScopeImpact[1].extension) {
                            if (extension.url === "number") {
                                if (extension.valuePositiveInt) {
                                    scope3number = extension.valuePositiveInt;
                                } else if (extension.valueQuantity) {
                                    scope3number = displayStringFromFHIRQuantity(extension.valueQuantity);
                                }
                            }
                            if (extension.url === "scope") {
                                scope3definition = simpleDisplayStringFromFHIRCodeableConcept(extension.valueCodeableConcept);
                            }
                        }
                    }
                }
                titlePageXhtml += "<tr><td><strong>Approximate " +
                    (numberOrPercent === "percent" ? "%" : "#") +
                    " Enrolled at Time of Sponsor Approval:</strong></td>";
                titlePageXhtml += "<td>";
                if (scope1number && scope1definition) {
                    titlePageXhtml += "<p>Approximately " +
                        scope1number + " enrolled " + scope1definition + "</p>";
                }
                if (scope2number && scope2definition) {
                    titlePageXhtml += "<p>Approximately " +
                        scope2number + " enrolled " + scope2definition + "</p>";
                }
                if (scope3number && scope3definition) {
                    titlePageXhtml += "<p>Approximately " +
                        scope3number + " enrolled " + scope3definition + "</p>";
                }
                titlePageXhtml += "</td></tr>";
                titlePageXhtml += "<tr><td><strong>Reason(s) for Amendment:</strong></td>";
                titlePageXhtml += "<td>Primary: " +
                    simpleDisplayStringFromFHIRCodeableConcept(amendmentPrimaryReason) +
                    "</td>";
                titlePageXhtml += "<td>Secondary: " +
                    getCodeableConceptDisplayForObjectOrArray(amendmentSecondaryReason) +
                    "</td></tr>";
                titlePageXhtml += "<tr><td><strong>Amendment Summary:</strong></td>";
                titlePageXhtml += "<td>" + amendmentSummary + "</td></tr>";
                titlePageXhtml += "<tr><td>Is this amendment likely to have a substantial impact on the safety or rights of the participants?</td>";
                titlePageXhtml += "<td>";
                titlePageXhtml += "<p>" +
                    simpleDisplayStringFromFHIRCodeableConcept(substantialImpactSafety) +
                    "</p>";
                if (substantialImpactSafetyComment) {
                    titlePageXhtml += "<p>" +
                        substantialImpactSafetyComment +
                        "</p>";
                }
                titlePageXhtml += "</td></tr>";
                titlePageXhtml += "<tr><td>Is this amendment likely to have a substantial impact on the reliability and robustness of the data generated in the clinical trial?</td>";
                titlePageXhtml += "<td>";
                titlePageXhtml += "<p>" +
                    simpleDisplayStringFromFHIRCodeableConcept(substantialImpactReliability) +
                    "</p>";
                if (substantialImpactReliabilityComment) {
                    titlePageXhtml += "<p>" +
                        substantialImpactReliabilityComment +
                        "</p>";
                }
                titlePageXhtml += "</td></tr>";
                titlePageXhtml += "</table>";
                titlePageXhtml += "<p><strong>Overview of Changes in the Current Amendment</strong></p>";
                if (amendmentRationale) {
                    titlePageXhtml += "<p>" + amendmentRationale + "</p>";
                }
                if (amendmentDescription) {
                    titlePageXhtml += "<p>" + amendmentDescription + "</p>";
                }
                if (amendmentDetails.length) {
                    titlePageXhtml += "<table>";
                    titlePageXhtml += "<tr><td><strong>Description of Change:</strong></td>" +
                        "<td><strong>Brief Rationale for Change:</strong></td>" +
                        "<td><strong>Section # and Name:</strong></td></tr>";
                    for (const detail of amendmentDetails) {
                        let description = "";
                        let rationale = "";
                        let section = "";
                        if (detail.extension.length) {
                            for (const ext of detail.extension) {
                                if (ext.url === "detail") {
                                    description = ext.valueString;
                                } else if (ext.url === "rationale") {
                                    rationale = ext.valueString;
                                } else if (ext.url === "section") {
                                    section = simpleDisplayStringFromFHIRCodeableConcept(ext.valueCodeableConcept);
                                }
                            }
                        }
                        titlePageXhtml += "<tr><td>" +
                            description + "</td><td>" +
                            rationale +
                            "</td><td>" +
                            section +
                            "</td></tr>"
                    }
                    titlePageXhtml += "</table>";
                }
            }
            titlePageXhtml += "</div>";
            setTitlePageState(prevState => {
                return {
                    ...prevState,
                    "status": "generated",
                    "titlePageXhtml": titlePageXhtml
                };
            });
            setRemount(prevState => prevState + 1);
        }
    }, [titlePageState.sponsorApprovalDate, titlePageState.sponsorSignatory]);

    useEffect(() => {
        if (m11ReportContainerFoi && !isNaN(m11ReportContainerFoi)) {
            loadResource(m11ReportContainerFoi, "M11ReportContainer", apiToken, dispatch);
        }
    }, [m11ReportContainerFoi]);

    useEffect(() => {
        if (m11ReportContainerResource &&
            Object.keys(m11ReportContainerResource).length > 0) {
            if (m11ReportContainerResource.section?.[0]?.text?.div &&
                m11ReportContainerResource.section[0].text.status !== "empty") {
                let startingTitlePageData = {
                    "status": m11ReportContainerResource.section[0].text.status,
                    "titlePageXhtml": m11ReportContainerResource.section[0].text.div,
                    "loaded": true
                };
                setTitlePageState(startingTitlePageData);
                setStartingTitlePageState(startingTitlePageData);
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportContainerResource]);

    return <div>
        <h3>Complete Title Page</h3>
        {titlePageState.loaded && <>
            <h4 style={{ marginBottom: "0px" }}>Enter the Sponsor Approval Date</h4>
            <p>Enter Sponsor Approval Date or State location where sponsor approval information can be found.</p>
            <StringEntry elementName='sponsorApprovalDate'
                fieldLabel='Sponsor Approval Date'
                startingValue={sponsorDataState.sponsorApprovalDate}
                setResourceState={setSponsorDataState} />
            <h4 style={{ marginBottom: "0px" }}>Enter the Sponsor Signatory (optional)</h4>
            <p>If desired, Enter sponsor signatory (e.g., name, title, signature and date, or State location where sponsor signatory information can be found (e.g., electronic signature).</p>
            <StringEntry elementName='sponsorSignatory'
                fieldLabel='Sponsor Signatory'
                startingValue={sponsorDataState.sponsorSignatory}
                setResourceState={setSponsorDataState} />
            <Button className="formButton navigationButton"
                style={{
                    color: "#000000",
                    fontSize: "20px",
                    width: "100%",
                    margin: "0px"
                }}
                content="Regenerate Title Page."
                onClick={() => {
                    setTitlePageState(prevState => {
                        return {
                            ...prevState,
                            "sponsorApprovalDate": sponsorDataState.sponsorApprovalDate,
                            "sponsorSignatory": sponsorDataState.sponsorSignatory
                        }
                    });
                }}
                disabled={!((sponsorDataState.sponsorApprovalDate &&
                    !titlePageState.titlePageXhtml.includes(sponsorDataState.sponsorApprovalDate)
                ) ||
                    (sponsorDataState.sponsorSignatory &&
                        !titlePageState.titlePageXhtml.includes(sponsorDataState.sponsorSignatory)
                    ))}
            />
            {titlePageState.titlePageXhtml && <>
                <h4 style={{ marginBottom: "0px" }}>Revise the Title Page as needed.</h4>
                {remount && <TextDivEntry elementName="titlePageXhtml" key={remount}
                    startingValue={titlePageState.titlePageXhtml}
                    setResourceState={setTitlePageState} />}
            </>}
        </>}
    </div >
});

const M11Step005CreateTitlePageHelperText = <div>
    <h3>Review Title Page</h3>
    <div style={{ textAlign: "left" }}>
        <p>Enter the Sponsor Approval Date</p>
        <div style={{ marginLeft: "24px" }}>
            <p>Enter Sponsor Approval Date or State location where sponsor approval information can be found.</p>
        </div>
        <p>Enter the Sponsor Signatory (optional)</p>
        <div style={{ marginLeft: "24px" }}>
            <p>If desired, Enter sponsor signatory (e.g., name, title, signature and date, or State location where sponsor signatory information can be found (e.g., electronic signature).</p>
            <p>If desiring to use an image for the Sponsor Signatory, enter text here then replace with an image in the generated Title Page.</p>
        </div>
        <p>Click the 'Regenerate Title Page.' button to recreate the Title Page using the values entered above.</p>
        <p>Review the auto-generated Title Page.</p>
        <p>Edit the Title Page as desired.</p>
        <p>To apply edits to the structured data (and not just the Title Page), go back to Previous steps.</p>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div>

// eslint-disable-next-line no-empty-pattern
const M11Step005TitlePageValidationCheck = ({ }) => {

    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [validationCheckState, setValidationCheckState] = useState({});

    useEffect(() => {
        if (m11ResearchStudyFoi) {
            validateStep(apiToken, 5, m11ResearchStudyFoi,
                setValidationCheckState)
        }
    }, [m11ResearchStudyFoi]);

    return <div>{validationCheckState?.success && <div>
        <h4>Step 5 (Complete Title Page) {validationCheckState.step05numberOfChecks} issues checked. {validationCheckState.step05numberPassed} passed. {validationCheckState.step05numberFailed} failed.</h4>
        <p>Title Page has content: {validationCheckState.resultCheckTitlePage}</p>
        <p>Title Page includes required heading 'Full Title:': {validationCheckState.resultCheckFullTitle}</p>
        <p>Title Page includes required heading 'Sponsor Protocol Identifier:': {validationCheckState.resultCheckSponsorProtocolIdentifierHeader}</p>
        <p>Title Page includes required heading 'Original Protocol:': {validationCheckState.resultCheckOriginalProtocol}</p>
        <p>Title Page includes required heading 'Trial Phase:': {validationCheckState.resultCheckTrialPhase}</p>
        <p>Title Page includes required heading 'Sponsor Name and Address:': {validationCheckState.resultCheckSponsor}</p>
        <p>Title Page includes required heading 'Regulatory or Clinical Trial Identifier(s):': {validationCheckState.resultCheckIdentifiers}</p>
        <p>Title Page includes required heading 'Sponsor Approval:': {validationCheckState.resultCheckApproval}</p>
        <p>Title Page includes required heading 'Amendment Details': {validationCheckState.resultCheckAmendmentDetailsHeading}</p>
        <p>Title Page includes valid value for Amendment Details: {validationCheckState.resultCheckAmendmentDetailsValue}</p>
    </div>}  </div>
}

export default M11Step005CreateTitlePage;
export { M11Step005CreateTitlePageHelperText, M11Step005TitlePageValidationCheck };